import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchProductById } from "../../../RTK/productSlice";

// Import popup components
import AgentPopup from "../product-popup/AgentPopup ";
import CreateAgentPopup from "../product-popup/CreateAgentPopup";

import {
  ArrowLeft,
  Users,
  UserPlus,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Package,
  DollarSign,
  Star,
  Clock,
  Loader2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Calculator,
  Eye,
} from "lucide-react";

// Agent Card Component
const AgentCard = ({ agent, index, multiplier, onAgentClick }) => {
  const finalPrice = agent.agent_price * multiplier;
  const isAvailable = agent.is_product_available;
  const isCheapest = index === 0 && isAvailable;

  return (
    <div
      onClick={() => onAgentClick(agent)}
      className={`relative p-4 rounded-xl border transition-all duration-300 hover:scale-[1.02] hover:shadow-lg cursor-pointer mb-3
        ${isAvailable
          ? isCheapest 
            ? "bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border-yellow-500/40 hover:border-yellow-500/60"
            : "bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/40 hover:border-green-500/60"
          : "bg-gradient-to-r from-red-500/20 to-pink-500/20 border-red-500/40 hover:border-red-500/60"
        }`}
    >
      {/* Badge الأرخص - absolute في الزاوية اليمنى العلوية */}
      {isCheapest && (
        <div className="absolute -top-2 -right-2 z-10">
          <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black text-xs px-3 py-1 rounded-full font-bold shadow-lg">
            الأرخص
          </div>
        </div>
      )}

      {/* Agent Info - تخطيط أفقي */}
      <div className="flex items-center justify-between">
        {/* اليسار - السعر والرقم */}
        <div className="flex items-center gap-3">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold
            ${isCheapest 
              ? 'bg-yellow-500 text-black' 
              : isAvailable 
                ? 'bg-green-500 text-white' 
                : 'bg-red-500 text-white'
            }`}
          >
            #{index + 1}
          </div>
          <div className="text-right">
            <div className={`font-bold text-lg ${isCheapest ? 'text-yellow-400' : 'text-white'}`}>
              ${finalPrice.toFixed(2)}
            </div>
            {/* {multiplier !== 1 && (
              <div className="text-xs text-gray-500 line-through">
                ${agent.agent_price.toFixed(2)}
              </div>
            )} */}
          </div>
        </div>

        {/* الوسط - معلومات الوكيل */}
        <div className="flex-1 text-center px-4">
          {/* <h3 className="text-white font-bold text-base mb-1">{agent.agent}</h3> */}
          <p className="text-gray-400 text-xs">ID: {agent.parent_id}</p>
        </div>

        <div className="flex items-center gap-2">
          <h3 className="text-white font-bold text-base mb-1">{agent.agent}</h3>
        </div>
      </div>

      {/* Hover Effect */}
      <div className="absolute inset-0 bg-white/5 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none" />
    </div>
  );
};


// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, color = "text-blue-400", bgColor = "bg-blue-500/10" }) => (
  <div className={`${bgColor} p-4 sm:p-6 rounded-xl border border-gray-700/50`}>
    <div className="flex items-center gap-3">
      <div className={`p-2 bg-gray-800/50 rounded-lg ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-gray-400 text-sm">{title}</p>
        <p className={`font-bold text-lg ${color}`}>{value}</p>
      </div>
    </div>
  </div>
);

// Main ProductAgents Component
const ProductAgents = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { selectedProduct, loading, error } = useSelector((state) => state.products);

  // States - إزالة sortBy
  const [multiplier, setMultiplier] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all, available, unavailable
  const [filteredAgents, setFilteredAgents] = useState([]);

  // Popup states
  const [showAgentPopup, setShowAgentPopup] = useState(false);
  const [showCreateAgentPopup, setShowCreateAgentPopup] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  useEffect(() => {
    if (id) {
      dispatch(fetchProductById(id));
    }
  }, [id, dispatch]);

  // Filter and sort agents - ترتيب ثابت بالسعر فقط
  useEffect(() => {
    if (!selectedProduct?.agent) {
      setFilteredAgents([]);
      return;
    }

    let filtered = [...selectedProduct.agent];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(agent =>
        agent.agent.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.parent_id.toString().includes(searchQuery)
      );
    }

    // Filter by status
    if (filterStatus !== "all") {
      filtered = filtered.filter(agent => {
        if (filterStatus === "available") return agent.is_product_available;
        if (filterStatus === "unavailable") return !agent.is_product_available;
        return true;
      });
    }

    // Sort agents by price only (الترتيب ثابت)
    filtered.sort((a, b) => {
      return (a.agent_price * multiplier) - (b.agent_price * multiplier);
    });

    setFilteredAgents(filtered);
  }, [selectedProduct?.agent, searchQuery, filterStatus, multiplier]);

  const handleBack = () => {
    navigate(`/product/${id}`);
  };

  const handleAgentClick = (agent) => {
    setSelectedAgent(agent);
    setShowAgentPopup(true);
  };

  const handleCreateAgent = () => {
    setShowCreateAgentPopup(true);
  };

  // Calculate stats
  const totalAgents = selectedProduct?.agent?.length || 0;
  const availableAgents = selectedProduct?.agent?.filter(agent => agent.is_product_available).length || 0;
  const unavailableAgents = totalAgents - availableAgents;
  const cheapestPrice = selectedProduct?.agent?.length > 0 
    ? Math.min(...selectedProduct.agent.filter(a => a.is_product_available).map(a => a.agent_price * multiplier))
    : 0;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
        <div className="flex flex-col items-center justify-center gap-4">
          <Loader2 className="w-16 h-16 text-yellow-500 animate-spin" />
          <p className="text-white text-lg text-center">جاري تحميل وكلاء المنتج...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !selectedProduct) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="bg-red-500/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <p className="text-white text-xl mb-4">لم يتم العثور على المنتج</p>
          <p className="text-gray-400 mb-6">{error || 'المنتج غير موجود أو تم حذفه'}</p>
          <button
            onClick={() => navigate('/product')}
            className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-3 rounded-xl transition-colors font-medium"
          >
            العودة للمنتجات
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" dir="rtl">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-yellow-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 sm:mb-8 gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <button
              onClick={handleBack}
              className="p-2 sm:p-3 bg-gray-800/80 hover:bg-gray-700/80 rounded-xl border border-gray-700/50 transition-all duration-300 group flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-white transition-colors" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent truncate">
                وكلاء المنتج
              </h1>
              <p className="text-gray-400 text-sm sm:text-base flex items-center gap-2 mt-1 sm:mt-2">
                <Package className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 flex-shrink-0" />
                <span className="text-xs sm:text-sm">{selectedProduct.name}</span>
              </p>
            </div>
          </div>
          
          <button
            onClick={handleCreateAgent}
            className="flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-xl transition-all duration-300 group flex-shrink-0"
          >
            <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:rotate-90" />
            <span className="text-sm sm:text-base">إضافة وكيل جديد</span>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <StatsCard
            title="إجمالي الوكلاء"
            value={totalAgents}
            icon={Users}
            color="text-blue-400"
            bgColor="bg-blue-500/10"
          />
          <StatsCard
            title="الوكلاء المتاحين"
            value={availableAgents}
            icon={CheckCircle}
            color="text-green-400"
            bgColor="bg-green-500/10"
          />
          <StatsCard
            title="الوكلاء غير المتاحين"
            value={unavailableAgents}
            icon={XCircle}
            color="text-red-400"
            bgColor="bg-red-500/10"
          />
          <StatsCard
            title="أقل سعر"
            value={cheapestPrice > 0 ? `$${(cheapestPrice).toFixed(2)}` : "غير محدد"}
            icon={TrendingDown}
            color="text-yellow-400"
            bgColor="bg-yellow-500/10"
          />
        </div>

        {/* Controls - مبسط بدون فلتر الترتيب */}
        <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                <input
                  type="text"
                  placeholder="البحث عن الوكلاء..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-gray-700/50 text-white pr-10 pl-4 py-2.5 rounded-xl border border-gray-600/50 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors placeholder-gray-400 text-sm sm:text-base"
                />
              </div>
            </div>

            {/* Filter by Status */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full bg-gray-700/50 text-white px-4 py-2.5 rounded-xl border border-gray-600/50 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors text-sm sm:text-base"
              >
                <option value="all">جميع الحالات</option>
                <option value="available">متوفر فقط</option>
                <option value="unavailable">غير متوفر فقط</option>
              </select>
            </div>

            {/* Multiplier */}
            <div>
              <div className="relative">
                <Calculator className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                <input
                  type="number"
                  min="1"
                  step="0.1"
                  placeholder="مضاعف السعر"
                  value={multiplier}
                  onChange={(e) => setMultiplier(Number(e.target.value) || 1)}
                  className="w-full bg-gray-700/50 text-white pr-10 pl-4 py-2.5 rounded-xl border border-gray-600/50 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors placeholder-gray-400 text-sm sm:text-base"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Agents List - تخطيط عمودي مثل الصورة */}
        <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-700/50 shadow-2xl p-4 sm:p-6">
          {filteredAgents.length === 0 ? (
            <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
              <div className="inline-block p-4 rounded-full bg-gray-800/50 mb-4">
                <Users className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-white text-xl font-bold mb-2">لا يوجد وكلاء</h3>
              <p className="text-gray-400 mb-6">
                {searchQuery || filterStatus !== "all"
                  ? "لا توجد نتائج تطابق معايير البحث"
                  : "لم يتم إضافة أي وكلاء لهذا المنتج بعد"
                }
              </p>
              {(!searchQuery && filterStatus === "all") && (
                <button
                  onClick={handleCreateAgent}
                  className="flex items-center gap-2 px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-xl transition-colors"
                >
                  <UserPlus className="w-5 h-5" />
                  إضافة وكيل جديد
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4  gap-4">
              {filteredAgents.map((agent, index) => (
                <AgentCard
                  key={agent.parent_id}
                  agent={agent}
                  index={index}
                  multiplier={multiplier}
                  onAgentClick={handleAgentClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Popups */}
      {showAgentPopup && selectedAgent && (
        <AgentPopup
          agent={selectedAgent}
          setShowAgentPopup={setShowAgentPopup}
        />
      )}
      
      {showCreateAgentPopup && (
        <CreateAgentPopup
          setShowPopup={setShowCreateAgentPopup}
          productId={selectedProduct?.id}
          agents={selectedProduct?.agents}
          agent={selectedProduct?.agent}
        />
      )}
    </div>
  );
};

export default ProductAgents;